import {getApiTypeFromGraphQLTypename} from 'nutshell-core/utils';

import type {EmptyStateProps} from 'shells/card/card-widget/card-widget';
import type {TabProps} from 'shells/control-bar/page-control-bar';
import {CalendarSquareRegularActivityIcon} from 'shells/icon/svg/calendar-square-regular-activity-icon';
import type {Participant as ParticipantMapped} from 'shells/timeline/types';

import type {
    ActivityForWidgetFragment,
    GetActivitiesForWidget_activities_participants as Participant,
} from 'nutshell-graphql-types';

export function getTabProps(
    scheduledActivities: ActivityForWidgetFragment[] | null | undefined,
    loggedActivities: ActivityForWidgetFragment[] | null | undefined,
    selectedTab: string,
    setSelectedTab: (tab: string) => void
): TabProps {
    return {
        options: [
            {
                id: 'scheduled',
                label: 'Scheduled',
                count: scheduledActivities ? String(scheduledActivities.length) : '0',
            },
            {
                id: 'logged',
                label: 'Logged',
                count: loggedActivities ? String(loggedActivities.length) : '0',
            },
        ],
        selectedTab: selectedTab,
        onChangeTab: (tab: string) => {
            setSelectedTab(tab);
        },
    };
}

export function getEmptyStateProps(
    scheduledActivities: ActivityForWidgetFragment[] | null | undefined,
    loggedActivities: ActivityForWidgetFragment[] | null | undefined,
    selectedTab: string
): EmptyStateProps | undefined {
    if (selectedTab === 'scheduled') {
        return {
            isEmpty: scheduledActivities ? scheduledActivities.length === 0 : true,
            text: 'No scheduled activities',
            icon: CalendarSquareRegularActivityIcon,
            iconSize: 22,
        };
    } else if (selectedTab === 'logged') {
        return {
            isEmpty: loggedActivities ? loggedActivities.length === 0 : true,
            text: 'No logged activities',
            icon: CalendarSquareRegularActivityIcon,
            iconSize: 22,
        };
    }
}

export function getIsTabLoading(
    isScheduledActivitiesLoading: boolean,
    isLoggedActivitiesLoading: boolean,
    selectedTab: string
): boolean {
    if (selectedTab === 'scheduled') {
        return isScheduledActivitiesLoading;
    } else if (selectedTab === 'logged') {
        return isLoggedActivitiesLoading;
    }

    return false;
}

export function getActivities(
    scheduledActivities: ActivityForWidgetFragment[] | null | undefined,
    loggedActivities: ActivityForWidgetFragment[] | null | undefined,
    selectedTab: string
): ActivityForWidgetFragment[] {
    if (!scheduledActivities || !loggedActivities) {
        return [];
    }

    if (selectedTab === 'scheduled') {
        return scheduledActivities;
    } else if (selectedTab === 'logged') {
        return loggedActivities;
    }

    return [];
}

export const getMappedParticipants = (participants: Participant[]): ParticipantMapped[] => {
    return participants.map((participant) => {
        return {
            type: getApiTypeFromGraphQLTypename(participant.__typename),
            id: participant.id,
            name: participant.name,
            avatarUrl: participant.avatarUrl,
            initials: typeof participant.initials === 'string' ? participant.initials : undefined,
            htmlUrl: typeof participant.htmlUrl === 'string' ? participant.htmlUrl : undefined,
            email: participant.primaryEmailAddress || undefined,
            phone: participant.primaryPhoneNumber || undefined,
        };
    });
};

export const setupActivityEventListeners = (
    handleActivityChange: () => void,
    includeLoggedEvent = true
) => {
    window.addEventListener('activityCreated', handleActivityChange);
    window.addEventListener('activityRelatedLeadsUpdated', handleActivityChange);
    if (includeLoggedEvent) {
        window.addEventListener('activityLogged', handleActivityChange);
    }
    window.addEventListener('activityDeleted', handleActivityChange);
};

export const removeActivityEventListeners = (
    handleActivityChange: () => void,
    includeLoggedEvent = true
) => {
    window.removeEventListener('activityCreated', handleActivityChange);
    window.removeEventListener('activityRelatedLeadsUpdated', handleActivityChange);
    if (includeLoggedEvent) {
        window.removeEventListener('activityLogged', handleActivityChange);
    }
    window.removeEventListener('activityDeleted', handleActivityChange);
};
